import { createFileRoute, Link } from "@tanstack/react-router";
import { Suspense } from "react";
import { Phone, MapPin, ShieldCheck, ArrowRight, CheckCircle2 } from "lucide-react";
import { PageHeader } from "./fordon";
import { FyndanuGrid, FyndanuGridSkeleton } from "@/components/FyndanuGrid";
import { fyndanuListingsQuery } from "@/lib/vehicles";
import { buildLocalStockHead } from "@/lib/local-seo";
import type { ListingLike } from "@/lib/listing-seo";

const URL_SELF = "https://www.akhusbilfritid.se/husvagn-kalix";

const FAQ = [
  {
    q: "Var kan jag köpa husvagn i Kalix?",
    a: "Hos AK Husbil & Fritid på Stabsvägen 5 i Kalix. Vi är familjeägda sedan 2004 och har ett rullande lager av kontrollerade husvagnar och husbilar. Ring 070-862 97 43 så bokar vi en visning.",
  },
  {
    q: "Vilka husvagnsmärken säljer ni?",
    a: "Vi tar in kvalitetskontrollerade begagnade husvagnar från etablerade märken som Kabe, Adria, Cabby, Polar, Hobby och Knaus. Utbudet växlar löpande — se lagret för det som står inne just nu.",
  },
  {
    q: "Fukttestar ni husvagnarna innan försäljning?",
    a: "Ja. Varje husvagn fuktmäts i väggar, golv och tak innan den läggs ut. Vi redovisar värdena för dig och berättar också vad som eventuellt behöver åtgärdas — inga överraskningar efter köpet.",
  },
  {
    q: "Vad kostar en begagnad husvagn i Norrbotten?",
    a: "Spannet är stort: enklare äldre vagnar runt 40 000–90 000 kr, medelålders familjevagnar 150 000–300 000 kr och yngre vintervagnar däröver. Se AK Prisindex för aktuella medianpriser per månad.",
  },
  {
    q: "Fungerar husvagnen för vintercamping i Norrbotten?",
    a: "Vintercertifierade vagnar med isolerat dubbelgolv, varmluftsystem och isolerade tankar klarar norrbottnisk vinter bra. Vi går igenom vilken standard just den vagn du tittar på har innan du bestämmer dig.",
  },
  {
    q: "Vilket körkort behöver jag för att dra husvagn?",
    a: "Med B-körkort får du dra ekipage upp till 3 500 kg totalvikt (bil + vagn). B96 ger upp till 4 250 kg och BE upp till 7 000 kg. Vi räknar tillsammans på din bils dragvikt innan du köper.",
  },
  {
    q: "Kan jag byta in min gamla husvagn?",
    a: "Ja, vi tar in inbyten. Ring så bokar vi en värdering — du får ett ärligt bud utifrån skick, fuktstatus, ålder och efterfrågan.",
  },
];

export const Route = createFileRoute("/husvagn-kalix")({
  loader: ({ context }) => context.queryClient.ensureQueryData(fyndanuListingsQuery),
  head: ({ loaderData }) =>
    buildLocalStockHead({
      category: "husvagn",
      items: (loaderData as { items?: ListingLike[] } | undefined)?.items ?? [],
      url: URL_SELF,
      city: "Kalix",
      faq: FAQ,
      breadcrumbName: "Husvagn Kalix",
      fallbackBrands: ["Kabe", "Adria", "Cabby", "Polar", "Knaus"],
    }),
  component: HusvagnKalixPage,
});

const LICENSES = [
  { label: "B", weight: "max 3 500 kg totalt", note: "Bil + vagn tillsammans. Räcker för lättare vagnar." },
  { label: "B96", weight: "max 4 250 kg totalt", note: "Kort utbildning + prov, ingen ny teoridel." },
  { label: "BE", weight: "max 7 000 kg totalt", note: "Egen förarutbildning och uppkörning." },
];

function HusvagnKalixPage() {
  return (
    <>
      <PageHeader
        label="Husvagn · Kalix · Norrbotten"
        title={
          <>
            Husvagn i <span className="font-italic-display text-accent">Kalix</span>.
          </>
        }
        sub="AK Husbil & Fritid säljer fukttestade begagnade husvagnar i Kalix sedan 2004. Vi mäter, dokumenterar och berättar precis vad du köper — sedan hjälper vi dig räkna på dragvikt, körkort och månadskostnad."
      />

      <section className="container-edge -mt-6 pb-4">
        <div className="flex flex-wrap gap-3">
          <Link
            to="/husvagnar-till-salu"
            className="inline-flex items-center gap-2 btn-shine rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
          >
            Se husvagnar i lager <ArrowRight size={14} />
          </Link>
          <a
            href="tel:0708629743"
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary"
          >
            <Phone size={16} /> Ring 070-862 97 43
          </a>
          <Link
            to="/kontakt"
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary"
          >
            <MapPin size={16} /> Boka visning i Kalix
          </Link>
        </div>
      </section>

      <section className="container-edge py-10 md:py-14">
        <p className="eyebrow">Lagret i Kalix just nu</p>
        <h2 className="mt-3 font-display text-3xl md:text-4xl">
          Husvagnar och husbilar <span className="font-italic-display text-accent">på plats</span> hos oss.
        </h2>
        <p className="mt-4 max-w-2xl text-muted-foreground">
          Allt du ser här står i Kalix. Ring innan du åker så ser vi till att vagnen är framme och att någon möter dig.
        </p>
        <div className="mt-8">
          <Suspense fallback={<FyndanuGridSkeleton />}>
            <FyndanuGrid initialLimit={6} hideTrustRow />
          </Suspense>
        </div>
      </section>


      <section className="container-edge py-16 md:py-20">
        <div className="grid gap-12 md:grid-cols-2">
          <div>
            <p className="eyebrow">Varför köpa husvagn hos oss</p>
            <h2 className="mt-3 font-display text-4xl md:text-5xl">Fukten mäts innan vagnen läggs ut.</h2>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
              Fuktskador är den enskilt största risken när man köper begagnad husvagn — och den syns sällan på
              bilderna. Därför mäter vi väggar, golv och tak på varje vagn vi tar in i Kalix, och redovisar värdena
              öppet för dig. Under 15 % är grönt, 15–20 % ska följas upp, över 20 % betyder åtgärd.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "Fuktprotokoll på varje husvagn — värden redovisas öppet",
                "Kontroll av kylskåp, värme, gasol och bromsar",
                "Kabe, Adria, Cabby, Polar, Hobby och Knaus i utbudet",
                "Inbyte, värdering och finansieringshjälp",
                "20+ år i Kalix — vi finns kvar efter affären",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-foreground/85">
                  <CheckCircle2 className="mt-0.5 shrink-0 text-accent" size={20} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl border border-border bg-card p-8 md:p-10">
            <ShieldCheck className="text-accent" size={32} />
            <h3 className="mt-6 font-display text-3xl">Besök oss i Kalix</h3>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              Stabsvägen 5, 952 51 Kalix. Vi öppnar enligt överenskommelse — ring först så ser vi till att vagnen du
              vill titta på står framme och är upplåst.
            </p>
            <dl className="mt-8 space-y-4 text-sm">
              <div className="flex justify-between border-b border-border pb-3">
                <dt className="text-muted-foreground">Adress</dt>
                <dd className="font-medium">Stabsvägen 5, Kalix</dd>
              </div>
              <div className="flex justify-between border-b border-border pb-3">
                <dt className="text-muted-foreground">Telefon</dt>
                <dd className="font-medium">070-862 97 43</dd>
              </div>
              <div className="flex justify-between border-b border-border pb-3">
                <dt className="text-muted-foreground">E-post</dt>
                <dd className="font-medium">info@akhusbilfritid.se</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Grundat</dt>
                <dd className="font-medium">2004</dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      <section className="bg-warm">
        <div className="container-edge py-16 md:py-20">
          <p className="eyebrow">Innan du köper</p>
          <h2 className="mt-3 font-display text-4xl md:text-5xl">
            Körkort och <span className="font-italic-display text-accent">dragvikt</span>.
          </h2>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground">
            Det vanligaste felköpet är en vagn som bilen inte får dra. Räkna alltid på bilens tillåtna släpvagnsvikt
            och ekipagets totalvikt — inte bara vagnens tjänstevikt.
          </p>
          <div className="mt-8 grid gap-5 sm:grid-cols-3">
            {LICENSES.map((l) => (
              <div key={l.label} className="rounded-2xl border border-border bg-card p-6">
                <p className="font-display text-3xl text-accent">{l.label}</p>
                <p className="mt-2 font-medium">{l.weight}</p>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{l.note}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/vardera-husbil"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary"
            >
              Värdera din husvagn <ArrowRight size={14} />
            </Link>
            <Link
              to="/prisindex"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary"
            >
              Se prisindex <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <section className="container-edge py-16 md:py-24">
        <p className="eyebrow">Vanliga frågor</p>
        <h2 className="mt-3 font-display text-4xl md:text-5xl">
          Husvagn i <span className="font-italic-display text-accent">Kalix</span> — vad du behöver veta.
        </h2>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {FAQ.map((f) => (
            <div key={f.q} className="rounded-2xl border border-border bg-card p-6">
              <h3 className="font-display text-xl">{f.q}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="container-edge pb-12">
        <div className="rounded-3xl border border-accent/25 bg-accent/5 p-6 md:p-8">
          <p className="eyebrow text-accent">Läs vidare</p>
          <h2 className="mt-3 font-display text-2xl md:text-3xl leading-tight">
            Så gör du <span className="font-italic-display text-accent">fukttestet</span> själv
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            Gränsvärden i procent, var mätaren luras och när du ska tacka nej till affären — hela protokollet vi
            själva använder på verkstaden.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/blogg/$slug"
              params={{ slug: "fukttest-husvagn-husbil-protokoll-guide" }}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-medium text-primary-foreground"
            >
              Läs fuktguiden <ArrowRight size={14} />
            </Link>
            <Link
              to="/husbil-kalix"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-medium transition hover:bg-secondary"
            >
              Husbil i Kalix <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <section className="container-edge pb-16 md:pb-24">
        <div className="rounded-3xl border border-border bg-foreground p-8 md:p-14 text-background">
          <p className="eyebrow" style={{ color: "hsl(var(--accent))" }}>
            Nästa steg
          </p>
          <h2 className="mt-3 font-display text-4xl md:text-6xl leading-[1.05]">Kom och titta på vagnen.</h2>
          <p className="mt-6 max-w-xl text-base leading-relaxed opacity-80">
            Ring så berättar vi vad som står inne just nu, vad fuktmätningen visade och vad vagnen kostar per månad.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/husvagnar-till-salu"
              className="inline-flex items-center gap-2 rounded-full bg-accent px-6 py-3.5 text-sm font-semibold text-accent-foreground hover:opacity-90"
            >
              Se husvagnar i lager <ArrowRight size={14} />
            </Link>
            <a
              href="tel:0708629743"
              className="inline-flex items-center gap-2 rounded-full border border-background/30 px-6 py-3.5 text-sm font-semibold hover:bg-background/10"
            >
              <Phone size={16} /> 070-862 97 43
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
